import React from 'react'
import clsx from 'clsx'
import kebabCase from 'lodash.kebabcase'
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'
import { ReactComponent as Line } from '@/assets/icons/long_wavy_line.svg'
import { ReactComponent as Arrow } from '@/assets/icons/thin-arrow-purple.svg'
import { ReactComponent as BlueCircle } from '@/assets/icons/happy_clients.svg'
import { ReactComponent as YellowCircle } from '@/assets/icons/half_yellow_circle.svg'
import { Image } from '@/components/atoms/Image/Image'
import { Paragraph } from '@/components/atoms/MarkdownElements/MarkdownElements'
import { HeroCaseStudiesProps } from './types'

export function HeroCaseStudies({ heading, stories, featured_label, subheading }: HeroCaseStudiesProps) {
  const renderMainStory = (story: HeroCaseStudiesProps['stories'][0]) => {
    if (!story.content) return null

    const { card_image, card_logo, card_title, card_teaser } = story.content

    return (
      <CustomLink
        link_url={{
          cached_url: story.full_slug,
          url: '',
          id: story.full_slug,
          fieldtype: '',
          linktype: ''
        }}
        is_external={false}
        new_tab={false}
        className="group"
      >
        <article className="flex flex-col items-start gap-16 max-w-[774px]">
          {card_image && (
            <Image
              fixed={false}
              fluid
              data={card_image}
              className="aspect-[1.5] rounded-[4px] w-full netbook:max-w-[774px]"
            />
          )}
          {card_logo && <Image fixed={false} fluid data={card_logo} className="h-40" />}
          <div className="text-text-dark-900 font-sans flex flex-col gap-16">
            {card_title && (
              <p className="text-headingS-mobile netbook:text-headingS font-bold font-poppins">{card_title}</p>
            )}
            {card_teaser && <p className="block text-bodyM">{card_teaser}</p>}
            <p className="text-bodyL font-bold text-text-brand-primary flex gap-4 items-center group-hover:text-primary-400">
              <span>Read the Story</span>
              <Arrow />
            </p>
          </div>
        </article>
      </CustomLink>
    )
  }

  const renderSubStories = (subStories: HeroCaseStudiesProps['stories']) =>
    subStories.map((story) => {
      if (!story.content) return null

      const { card_logo, card_title } = story.content

      return (
        <>
          <CustomLink
            link_url={{
              cached_url: story.full_slug,
              url: '',
              id: story.full_slug,
              fieldtype: '',
              linktype: ''
            }}
            is_external={false}
            new_tab={false}
            className="group"
          >
            <article className="flex items-start flex-col gap-16">
              {card_logo && <Image fixed={false} fluid data={card_logo} className="h-40" />}
              <div className="text-text-dark-900 font-sans flex flex-col gap-16">
                {card_title && <p className="text-bodyL font-bold font-poppins">{card_title}</p>}
                <p className="text-bodyL font-bold text-text-brand-primary flex gap-4 items-center group-hover:text-primary-400">
                  <span>Read the Story</span>
                  <Arrow />
                </p>
              </div>
            </article>
          </CustomLink>
          <div className="h-2 w-full content-[''] bg-primary-300 last:hidden my-auto" />
        </>
      )
    })

  return (
    <section className={clsx('relative bg-primary-100 overflow-hidden')} id={kebabCase(heading)}>
      <BlueCircle className="absolute size-106 -bottom-32 -right-32 netbook:-bottom-0 netbook:-right-0" />
      <YellowCircle className="hidden netbook:block absolute top-1/4 left-0" />
      <Line className="hidden netbook:block absolute top-0 left-0 z-0 w-full -rotate-3" />
      <Line className="hidden netbook:block absolute bottom-0 right-0 netbook:-right-1/4 w-full z-0 -rotate-180" />
      <div className="flex flex-col p-24 mx-auto laptop:px-90 netbook:py-64 max-w-screen-desktop *:z-10">
        <h1 className="text-headingL-mobile netbook:text-headingL font-bold font-poppins text-center text-text-dark-900 mb-16">
          {heading}
        </h1>
        {subheading && (
          <Paragraph className="mx-auto text-center text-text-dark-600 text-bodyM netbook:text-bodyL mb-24 tablet:max-w-screen-tablet-lg tablet:mb-40">
            {subheading}
          </Paragraph>
        )}
        {featured_label && (
          <h2 className="text-headingM-mobile netbook:text-headingM font-bold font-poppins text-start text-text-dark-900 mb-16 netbook:mb-24">
            {featured_label}
          </h2>
        )}
        <div className="flex flex-col netbook:flex-row gap-24 netbook:gap-40">
          <div className="grid grid-cols-1">{stories?.[0] && renderMainStory(stories[0])}</div>
          <div className="flex content-[''] h-2 w-full bg-primary-300 netbook:hidden" />
          <div className="hidden netbook:flex content-[''] min-h-full w-2 bg-primary-300" />
          <div className="flex flex-col gap-24 netbook:gap-32 min-h-full netbook:max-w-[403px]">
            {renderSubStories(stories?.slice(1, 4))}
          </div>
        </div>
      </div>
    </section>
  )
}
